import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Check } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { TestimonialCarousel } from "@/components/TestimonialCarousel";
import { ReviewForm } from "@/components/ReviewForm";
import { BookButton } from "@/components/PrimaryCTA";
import { ABOUT, IMAGES } from "@/content";

function GetInTouch() {
  const ic = "w-full rounded-lg bg-ink-900 border border-white/12 px-4 py-3 text-ice placeholder:text-mist focus:border-cyan/60 outline-none";
  const [f, setF] = useState({ name: "", email: "", message: "", topic: "General question" });
  const [state, setState] = useState("form");
  const [error, setError] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setState("loading"); setError("");
    try { await api.post("/contact", f); setState("done"); }
    catch { setError("Something went wrong. Please try again."); setState("form"); }
  };
  return (
    <section className="relative py-24" data-testid="get-in-touch">
      <div className="mx-auto max-w-2xl px-5 sm:px-8">
        <Reveal>
          <div className="rounded-3xl glass p-8 sm:p-10">
            {state === "done" ? (
              <div className="text-center py-8" data-testid="get-in-touch-done">
                <div className="mx-auto h-16 w-16 rounded-full bg-cyan/15 flex items-center justify-center mb-5"><Check className="text-cyan" size={30} /></div>
                <h3 className="font-display text-3xl font-bold text-white">Message sent.</h3>
                <p className="text-ice/75 mt-3">Thank you for reaching out — I'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <span className="label text-cyan">Get in touch</span>
                <h2 className="font-display text-3xl font-bold text-white mt-2">Say hello</h2>
                <p className="text-ice/75 mt-4 leading-relaxed">If you have any questions about the classes, would like to leave feedback, enquire about a trial class for your studio/gym or anything else, then please write to us below…</p>
                <div className="space-y-3 mt-6">
                  <input data-testid="contact-name" required placeholder="Your name" className={ic} value={f.name} onChange={(e) => set("name", e.target.value)} />
                  <input data-testid="contact-email" required type="email" placeholder="Email" className={ic} value={f.email} onChange={(e) => set("email", e.target.value)} />
                  <select data-testid="contact-topic" className={ic} value={f.topic} onChange={(e) => set("topic", e.target.value)}>
                    {["General question", "Feedback", "Trial class for my studio/gym", "Something else"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <textarea data-testid="contact-message" required rows={5} placeholder="Your message" className={ic} value={f.message} onChange={(e) => set("message", e.target.value)} />
                </div>
                {error && <p className="text-red-300 text-sm mt-3" data-testid="contact-error">{error}</p>}
                <button data-testid="contact-submit" type="submit" disabled={state === "loading"} className="book-cta w-full justify-center mt-5 disabled:opacity-50">
                  {state === "loading" ? <Loader2 className="animate-spin" size={16} /> : "Send message"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setReviews(r.data.filter((t) => t.review_id))).catch(() => {}); }, []);
  return (
    <Layout page="about">
      <section className="relative pt-40 pb-16 overflow-hidden">
        <div className="absolute inset-0 mesh-top" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid md:grid-cols-5 gap-10 items-end">
          <div className="md:col-span-3">
            <span className="label">About</span>
            <h1 className="font-display font-black text-white tracking-tighter mt-4 leading-[0.95]" style={{ fontSize: "clamp(3rem, 9vw, 7rem)" }}>Alexia,<br />Founder</h1>
          </div>
          <div className="md:col-span-2 relative">
            <div className="absolute inset-0 bg-electric/15 blur-3xl rounded-full" />
            <img src={IMAGES.about} alt="Alexia, Founder" className="faded-media-soft relative rounded-3xl w-full object-cover border border-white/10 aspect-[3/4]" />
          </div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {ABOUT.paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className={`${i === 0 ? "" : "mt-8"} leading-relaxed ${i === 0 ? "font-display text-2xl sm:text-3xl font-medium text-white leading-snug" : "text-ice/75 text-lg"}`}>{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* A blend of movement backgrounds — video */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 mesh-top opacity-50" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><span className="label">A blend of movement backgrounds</span></Reveal>
          <Reveal delay={0.05}>
            <div className="mt-8 relative overflow-hidden rounded-3xl border border-white/10 aspect-video bg-ink-800" data-testid="about-video">
              <video
                className="absolute inset-0 h-full w-full object-cover faded-media"
                autoPlay muted loop playsInline preload="metadata" poster="/media/posters/107.jpg"
              >
                <source src="/media/videos/107.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900/55 via-transparent to-ink-900/10" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <TestimonialCarousel items={[...ABOUT.testimonials, ...reviews]} testId="about-testimonials" />
        </div>
      </section>

      <GetInTouch />

      <section className="relative pb-20" data-testid="about-review-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 text-center">
          <span className="label">Been to a class?</span>
          <div className="mt-6"><ReviewForm page="about" /></div>
        </div>
      </section>

      <section className="relative pb-32 text-center">
        <Reveal><div className="flex justify-center"><BookButton>Book a class</BookButton></div></Reveal>
      </section>
    </Layout>
  );
}
